import {useCallback, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {toast} from 'sonner';
import {ApiError, useGetCampaignById, usePublishCampaign} from '@tinct/core-api';
import {
  useCurrentWorkspaceUrlPrefix,
  useGetCurrentQuotas,
  useGetCurrentWorkspaceId,
} from '@tinct/core-workspace';
import {useOpenPaywall} from '@tinct/web-feature-payment';
import {Step7PublishContent} from '../step7-publish';
import {LaunchCampaignModal} from '../modals/launch-campaign-modal';

type UseCreateStep7PublishOptions = {
  campaignId: string;
};

export const useCreateStep7Publish = ({campaignId}: UseCreateStep7PublishOptions) => {
  const navigate = useNavigate();
  const currentWorkspaceUrlPrefix = useCurrentWorkspaceUrlPrefix();
  const {lpPublicationQuota} = useGetCurrentQuotas();
  const {openPaywall} = useOpenPaywall();
  const workspaceId = useGetCurrentWorkspaceId();
  const [isPublishing, setIsPublishing] = useState(false);
  const [showLaunchModal, setShowLaunchModal] = useState(false);

  const {data: campaign, isLoading} = useGetCampaignById(
    {
      id: campaignId,
      fields: {
        id: null,
        name: null,
        status: null,
        landingPageOriginType: null,
        landingPageVariationAxis: null,
        pages: {
          limit: 1000,
          fields: {
            id: null,
            status: null,
            outdatedStatus: null,
            targetType: null,
            adCampaign: { fields: { id: null, name: null, integrationId: null } },
            lead: {
              fields: {
                id: null,
                company: {
                  fields: {
                    id: null,
                    name: null,
                    logo: null,
                  },
                },
              },
            },
          },
        },
      },
    },
    {
      enabled: !!campaignId,
      refetchOnMount: 'always',
      refetchInterval: (query) => {
        const hasInflight = (query.state.data?.pages ?? []).some(
          (p) =>
            p.status === 'generating' ||
            p.status === 'queued' ||
            p.status === 'waiting'
        );
        return hasInflight ? 3000 : false;
      },
    }
  );

  const pages = useMemo(() => campaign?.pages ?? [], [campaign?.pages]);
  const isAdwordAxis = campaign?.landingPageVariationAxis === 'adword';
  const readyCount = pages.filter((p) => p.status === 'ready').length;
  // Already-published pages whose content changed need to be pushed live again.
  const republishCount = pages.filter(
    (p) => p.outdatedStatus === 'needs_republication'
  ).length;
  // Taken offline (page or whole campaign): a publish brings them back live.
  // Without this bucket a campaign that was unpublished could never relaunch.
  const unpublishedCount = pages.filter((p) => p.status === 'unpublished').length;
  const publishableCount = readyCount + republishCount + unpublishedCount;
  const excludedCount = pages.filter(
    (p) => p.status === 'excluded' || p.status === 'excluded_conflict'
  ).length;
  const waitingCount = pages.filter(
    (p) => p.status === 'generating' || p.status === 'waiting' || p.status === 'error'
  ).length;
  const liveInSyncCount = pages.filter(
    (p) => p.status === 'published' && p.outdatedStatus !== 'needs_republication'
  ).length;

  const publishableCompanyNames = useMemo(
    () =>
      pages
        .filter(
          (p) =>
            p.status === 'ready' ||
            p.status === 'unpublished' ||
            p.outdatedStatus === 'needs_republication'
        )
        .map((p) => p.lead?.company?.name ?? p.adCampaign?.name)
        .filter((name): name is string => !!name),
    [pages]
  );

  // Why "Next step" is disabled, for the button tooltip. ADWORD campaigns can
  // always launch (see canLaunch), so they never get a reason.
  const launchBlockedReason: string | null = (() => {
    if (isLoading || isAdwordAxis || publishableCount > 0) return null;
    if (waitingCount > 0) return 'Wait for the remaining pages to be generated';
    if (pages.length === 0) return 'No page to publish yet';
    if (liveInSyncCount > 0 && excludedCount + liveInSyncCount === pages.length)
      return 'Every page is already live and up to date';
    if (excludedCount === pages.length) return 'Every page is excluded from publishing';
    return 'No page is ready to publish';
  })();

  const {mutateAsync: publishCampaign} = usePublishCampaign();

  const launchAndContinue = useCallback(() => {
    if ((lpPublicationQuota?.remainingQuota ?? 0) < publishableCount) {
      openPaywall('not_enough_live_pages', {
        totalPages: String(publishableCount),
        campaignId,
      });
      return;
    }
    setShowLaunchModal(true);
  }, [campaignId, lpPublicationQuota?.remainingQuota, openPaywall, publishableCount]);

  const handleConfirmLaunch = useCallback(async () => {
    setIsPublishing(true);
    try {
      await publishCampaign({id: campaignId, organizationId: workspaceId});
      setShowLaunchModal(false);
      navigate(
        `${currentWorkspaceUrlPrefix}/funnel-campaigns/${campaignId}/publishing`,
        {replace: true}
      );
    } catch (error) {
      // Publishing is the product's most important action — never fail silently.
      toast.error(
        (error as ApiError).error ||
          'Could not publish the campaign. Please try again.',
      );
      setIsPublishing(false);
    }
  }, [
    campaignId,
    currentWorkspaceUrlPrefix,
    navigate,
    publishCampaign,
    workspaceId,
  ]);

  const component = useCallback(
    () => (
      <>
        <Step7PublishContent
          pages={pages}
          readyCount={readyCount}
          republishCount={republishCount}
          unpublishedCount={unpublishedCount}
          excludedCount={excludedCount}
          waitingCount={waitingCount}
          isNewOrigin={campaign?.landingPageOriginType === 'new'}
          isAdwordAxis={isAdwordAxis}
        />
        <LaunchCampaignModal
          open={showLaunchModal}
          onOpenChange={setShowLaunchModal}
          companyNames={publishableCompanyNames}
          readyCount={publishableCount}
          isPublishing={isPublishing}
          onLaunch={handleConfirmLaunch}
          isAdwordAxis={isAdwordAxis}
        />
      </>
    ),
    [
      pages,
      readyCount,
      republishCount,
      unpublishedCount,
      publishableCount,
      excludedCount,
      waitingCount,
      showLaunchModal,
      publishableCompanyNames,
      isPublishing,
      handleConfirmLaunch,
      campaign?.landingPageOriginType,
      isAdwordAxis,
    ]
  );

  return {
    component,
    launchAndContinue,
    isPublishing,
    // ADWORD campaigns can launch with zero variants: the campaign goes live
    // and starts listening — matched ads campaigns bring variants later.
    canLaunch: !isLoading && (publishableCount > 0 || isAdwordAxis),
    launchBlockedReason,
  };
};
